import { FunctionalArea } from '@/components/FunctionalArea';
import { Map } from '@/components/Map';
import { manrope } from '@/constants/font';
import { useReduxBusQueryManager } from '@/utils/query-manager/useReduxBusQueryManager';
import { twMerge } from 'tailwind-merge';
import { useEffect, useRef } from 'react';
import { PluginsManager } from '@/services/pluginsManager';
import { Modal } from '@/components/FunctionalArea/Modal';
import { useSelector } from 'react-redux';
import { publicPluginsListSelector } from '@/redux/plugins/plugins.selectors';
import { RootState } from '@/redux/store';
import axios from 'axios';
import md5 from 'crypto-js/md5';
import { ZERO } from '@/constants/common';
import { showToast } from '@/utils/showToast';
import { PLUGIN_LOADING_ERROR_PREFIX } from '@/components/Map/Drawer/AvailablePluginsDrawer/AvailablePluginsDrawer.constants';
import { getErrorMessage } from '@/utils/getErrorMessage';
import { useInitializeStore } from '@/utils/initialize/useInitializeStore';
import { openPluginsDrawer } from '@/redux/plugins/plugins.slice';
import { useAppDispatch } from '@/redux/hooks/useAppDispatch';
import { PROJECT_ID } from '@/constants';
import { CookieBanner } from '../FunctionalArea/CookieBanner';
import { ContextMenu } from '../FunctionalArea/ContextMenu';

export const MinervaSPA = (): JSX.Element => {
  const dispatch = useAppDispatch();
  useInitializeStore();
  useReduxBusQueryManager();

  const publicPlugins = useSelector(publicPluginsListSelector);
  const pluginsLoaded = useSelector((state: RootState) => state.plugins.pluginsLoaded);
  const prevPluginsLoadedRef = useRef<boolean>(pluginsLoaded);

  useEffect(() => {
    if (!prevPluginsLoadedRef.current && pluginsLoaded === true) {
      publicPlugins.forEach(async plugin => {
        if (plugin.defaultInProject.includes('*') || plugin.defaultInProject.includes(PROJECT_ID)) {
          const pluginUrl = plugin.urls[ZERO];
          try {
            const response = await axios(pluginUrl);
            let pluginScript = response.data;

            const hash = md5(pluginScript).toString();

            if (
              Array.isArray(PluginsManager.activePlugins[hash]) &&
              PluginsManager.activePlugins[hash].length > ZERO
            ) {
              // plugin already loaded
              return;
            }

            PluginsManager.setHashedPlugin({
              pluginUrl,
              pluginScript,
            });

            pluginScript += `//# sourceURL=${pluginUrl}`;

            /* eslint-disable no-new-func */
            const loadPlugin = new Function(pluginScript);

            loadPlugin();
            dispatch(openPluginsDrawer());
          } catch (error) {
            const errorMessage = getErrorMessage({ error, prefix: PLUGIN_LOADING_ERROR_PREFIX });
            showToast({
              type: 'error',
              message: errorMessage,
            });
          }
        }
      });
      prevPluginsLoadedRef.current = pluginsLoaded;
    }
  }, [dispatch, pluginsLoaded, publicPlugins]);

  useEffect(() => {
    const unsubscribe = PluginsManager.init();

    return () => unsubscribe();
  }, []);

  return (
    <div className={twMerge('relative', manrope.variable)}>
      <FunctionalArea />
      <Map />
      <Modal />
      <ContextMenu />
      <CookieBanner />
    </div>
  );
};
